<script setup lang="ts">
import flagGB from '~/assets/svg/flag-gb.svg?url';
import flagSE from '~/assets/svg/flag-se.svg?url';
import flagDE from '~/assets/svg/flag-de.svg?url';
import flagPT from '~/assets/svg/flag-pt.svg?url';
import flagAT from '~/assets/svg/flag-at.svg?url';

const props = defineProps({
    id: {
        type: String,
        default: () => '',
    },
    class: {
        type: String,
        default: () => '',
    },
});

const { locale } = useI18n();
const route = useRoute();
const { market: currentMarket, language: currentLanguage } = useMarket();
const { marketToAvailableLocales, localeToLanguage, marketToDefaultLocale, markets } =
    useAppConfig();

// Market configuration with flags and names
const marketConfig: Record<Market, { name: string; icon: string }> = {
    uk: { name: 'United Kingdom', icon: flagGB },
    se: { name: 'Sweden', icon: flagSE },
    de: { name: 'Germany', icon: flagDE },
    at: { name: 'Austria', icon: flagAT },
    pt: { name: 'Portugal', icon: flagPT },
};

// Current market display
const currentMarketDisplay = computed(() => {
    return marketConfig[currentMarket.value] || null;
});

// All available markets
const allMarkets = computed<Market[]>(() => {
    return Object.keys(markets) as Market[];
});

// Get the best locale for a target market, preferring current language
function getLocaleForMarket(targetMarket: Market): Locale {
    const availableLocales = marketToAvailableLocales[targetMarket] || [];

    // First, try to find a locale with the same language as current
    for (const loc of availableLocales) {
        if (localeToLanguage[loc as Locale] === currentLanguage.value) {
            return loc as Locale;
        }
    }

    // Fallback to market's default locale
    return marketToDefaultLocale[targetMarket] || 'en-gb';
}

// Build the path for switching to a different market
function getMarketSwitchPath(targetMarket: Market): string {
    const newLocale = getLocaleForMarket(targetMarket);
    // Get current path without locale prefix
    const path = route.path;
    const currentLocale = locale.value;

    // Remove current locale prefix and add new one
    let pathWithoutLocale = path;
    if (path.startsWith(`/${currentLocale}/`)) {
        pathWithoutLocale = path.substring(currentLocale.length + 1);
    } else if (path === `/${currentLocale}`) {
        pathWithoutLocale = '/home';
    }

    // Ensure path starts with /
    if (!pathWithoutLocale.startsWith('/')) {
        pathWithoutLocale = '/' + pathWithoutLocale;
    }

    return `/${newLocale}${pathWithoutLocale}`;
}

function closeDropdown() {
    const element = document.getElementById('market-dropdown');
    if (element) {
        element.classList.remove('block');
        element.classList.add('hidden');
    }
}
</script>

<template>
    <button
        type="button"
        data-dropdown-toggle="market-dropdown"
        class="inline-flex items-center text-gray-300 font-medium rounded-lg text-sm px-2.5 lg:px-5 py-2.5 mr-2 hover:bg-gray-800"
    >
        <div class="mr-1">
            <svg
                width="18px"
                height="18px"
                viewBox="0 0 18 18"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
            >
                <title>icon-position-grey</title>
                <g
                    id="icons/ad-details/icon-position-grey-copy"
                    stroke="none"
                    stroke-width="1"
                    fill="none"
                    fill-rule="evenodd"
                >
                    <path
                        id="Path"
                        d="M9,0 C5.69175,0 3,2.69175 3,6 C3,11.29275 8.485125,17.6048438 8.71875,17.8710938 C8.79,17.9524687 8.892,18 9,18 C9.11625,17.992125 9.21,17.9535937 9.28125,17.8710938 C9.514875,17.6003438 15,11.179875 15,6 C15,2.69175 12.30825,0 9,0 Z"
                        fill="#999999"
                    ></path>
                    <path
                        id="Path"
                        d="M9,4.125 C10.242375,4.125 11.25,5.132625 11.25,6.375 C11.25,7.617375 10.242375,8.625 9,8.625 C7.757625,8.625 6.75,7.617375 6.75,6.375 C6.75,5.132625 7.757625,4.125 9,4.125 Z"
                        fill="#FFFFFF"
                    ></path>
                </g>
            </svg>
        </div>
        {{ currentMarketDisplay?.name || 'Select market' }}
        <svg
            class="ml-1 w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
            ></path>
        </svg>
    </button>

    <div
        id="market-dropdown"
        class="hidden z-50 my-4 w-48 text-base list-none rounded divide-y divide-gray-100 shadow-xl bg-gray-800"
        @click="closeDropdown"
    >
        <ul class="py-1" role="none">
            <li v-for="mkt in allMarkets" :key="mkt">
                <a
                    :href="getMarketSwitchPath(mkt)"
                    class="block py-2 px-4 text-sm text-gray-400 hover:bg-gray-600 hover:text-white"
                    role="menuitem"
                >
                    <div class="inline-flex items-center">
                        <img
                            class="h-3.5 w-3.5 mr-2"
                            :src="marketConfig[mkt].icon"
                            :alt="`${marketConfig[mkt].name} flag`"
                        />
                        {{ marketConfig[mkt].name }}
                    </div>
                </a>
            </li>
        </ul>
    </div>
</template>
